import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Input, Label, Spinner, Table } from "reactstrap";
import { API_TIMEOUT, MAKAN_MINUM_DASHBOARD } from "../../utils";
import { BULAN_OPTIONS, bulanLabel } from "./laporanBulananConstants";
import s from "./belanjaMamin.module.scss";

const formatRupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const formatPercent = (n) => {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${Number(n).toFixed(1)}%`;
};

const progressColor = (pct) => {
  if (pct == null) return "#94a3b8";
  if (pct >= 80) return "#059669";
  if (pct >= 50) return "#d97706";
  return "#dc2626";
};

const ProgressBar = ({ value }) => {
  const pct = value != null && Number.isFinite(value) ? Math.min(value, 100) : 0;
  const color = progressColor(value);
  return (
    <div className={s.progressTrack}>
      <div
        className={s.progressFill}
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
      <span className={s.progressLabel}>{formatPercent(value)}</span>
    </div>
  );
};

const MakanMinumDashboardPage = () => {
  const currentYear = new Date().getFullYear();
  const [tahun, setTahun] = useState(String(currentYear));
  const [bulan, setBulan] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const listTahun = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 2018; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  const periodeLabel = useMemo(() => {
    if (!bulan) return `Tahun ${tahun}`;
    return `${bulanLabel(bulan)} ${tahun}`;
  }, [tahun, bulan]);

  useEffect(() => {
    if (!tahun) return;
    setLoading(true);
    const params = { tahun };
    if (bulan) params.bulan = bulan;
    axios
      .get(MAKAN_MINUM_DASHBOARD, { timeout: API_TIMEOUT, params })
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tahun, bulan]);

  const ringkasan = data?.ringkasan;
  const rows = data?.per_pd || [];

  return (
    <div className={`animated fadeIn ${s.pageWrap}`}>
      <div className={s.heroSection}>
        <h2>Dashboard Penyerapan Mamin</h2>
        <p>
          Rekap total belanja makan-minum, penyerapan pelaksanaan, dan persentase
          belanja yang dikelola pengelola desil 1–2 seluruh perangkat daerah.
        </p>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>Filter periode — {periodeLabel}</div>
        <div className={s.cardBody}>
          <div className={s.toolbar}>
            <div>
              <Label for="dash-tahun">Tahun anggaran</Label>
              <Input
                id="dash-tahun"
                type="select"
                value={tahun}
                onChange={(e) => setTahun(e.target.value)}
                style={{ minWidth: 140 }}
              >
                {listTahun.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Input>
            </div>
            <div>
              <Label for="dash-bulan">Bulan penyerapan</Label>
              <Input
                id="dash-bulan"
                type="select"
                value={bulan}
                onChange={(e) => setBulan(e.target.value)}
                style={{ minWidth: 180 }}
              >
                <option value="">Semua bulan</option>
                {BULAN_OPTIONS.map((b) => (
                  <option key={b.value} value={String(b.value)}>
                    {b.label}
                  </option>
                ))}
              </Input>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <div className={s.summaryRow}>
              <div className={s.summaryCard}>
                <div className={s.label}>Total belanja mamin</div>
                <div className={s.value}>
                  {formatRupiah(ringkasan?.total_belanja_mamin)}
                </div>
                <div className={s.summaryHint}>
                  Anggaran tahunan ({ringkasan?.jumlah_pd_ada_anggaran ?? 0} PD)
                </div>
              </div>
              <div className={s.summaryCard}>
                <div className={s.label}>Total penyerapan</div>
                <div className={s.value}>
                  {formatRupiah(ringkasan?.total_penyerapan)}
                </div>
                <div className={s.summaryHint}>
                  Dari laporan bulanan ({ringkasan?.jumlah_pd_ada_penyerapan ?? 0}{" "}
                  PD)
                </div>
              </div>
              <div className={s.summaryCard}>
                <div className={s.label}>Progres penyerapan</div>
                <div className={s.value}>
                  {formatPercent(ringkasan?.progres_penyerapan)}
                </div>
                <ProgressBar value={ringkasan?.progres_penyerapan} />
              </div>
              <div className={s.summaryCard}>
                <div className={s.label}>Belanja desil 1 &amp; 2</div>
                <div className={s.value}>
                  {formatPercent(ringkasan?.persentase_desil_12)}
                </div>
                <div className={s.summaryHint}>
                  {formatRupiah(ringkasan?.belanja_desil_12)} dari penyerapan
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>
          Per perangkat daerah — {periodeLabel}
        </div>
        <div className={`${s.cardBody} ${s.tableWrap}`}>
          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted mb-0">
              Belum ada data perangkat daerah.
            </p>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Perangkat daerah</th>
                  <th className="text-end">Total belanja mamin</th>
                  <th className="text-end">Total penyerapan</th>
                  <th style={{ minWidth: 160 }}>Progres penyerapan</th>
                  <th className="text-end">Desil 1 &amp; 2</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id_perangkat_daerah}>
                    <td>{idx + 1}</td>
                    <td>{row.nama_dinas}</td>
                    <td className="text-end">
                      {formatRupiah(row.total_belanja_mamin)}
                    </td>
                    <td className="text-end fw-semibold">
                      {formatRupiah(row.total_penyerapan)}
                    </td>
                    <td>
                      <ProgressBar value={row.progres_penyerapan} />
                    </td>
                    <td className="text-end">
                      <div>{formatPercent(row.persentase_desil_12)}</div>
                      <div className="small text-muted">
                        {formatRupiah(row.belanja_desil_12)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={2} className="text-end">
                    Total keseluruhan
                  </td>
                  <td className="text-end">
                    {formatRupiah(ringkasan?.total_belanja_mamin)}
                  </td>
                  <td className="text-end">
                    {formatRupiah(ringkasan?.total_penyerapan)}
                  </td>
                  <td>
                    <ProgressBar value={ringkasan?.progres_penyerapan} />
                  </td>
                  <td className="text-end">
                    {formatPercent(ringkasan?.persentase_desil_12)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default MakanMinumDashboardPage;
